// ─── Grand Memories Print Utility ─────────────────────────────────────────────
// All documents pull live branding (hotel name, logo, contact) from localStorage
// so that whatever is set in Admin → Branding appears on every printed document.

// Try v2 first (current key), fall back to v1 for backward compat
const STORAGE_KEY = 'grand_theme_v4';
const STORAGE_KEY_LEGACY = 'grand_theme_v2';

// ─── Approval Stamp Storage Keys (per role) ───────────────────────────────────
const STAMP_KEY = 'grand_approval_stamp_v1'; // legacy / generic fallback

// Role-specific stamp keys
const ROLE_STAMP_KEYS = {
  storekeeper:         'grand_stamp_storekeeper_v1',
  accountant:          'grand_stamp_accountant_v1',
  manager:             'grand_stamp_manager_v1',
  restaurant_manager:  'grand_stamp_manager_v1',
  general_manager:     'grand_stamp_manager_v1',
  director:            'grand_stamp_director_v1',
  admin:               'grand_stamp_director_v1',
  hr:                  'grand_stamp_manager_v1',
};

// Role labels shown inside the SVG fallback stamp
const ROLE_STAMP_LABELS = {
  storekeeper:        'STOREKEEPER',
  accountant:         'ACCOUNTANT',
  manager:            'MANAGER',
  restaurant_manager: 'MANAGER',
  general_manager:    'MANAGER',
  director:           'DIRECTOR',
  admin:              'DIRECTOR',
  hr:                 'MANAGER',
};

export function saveApprovalStamp(dataUrl, role) {
  try {
    const key = (role && ROLE_STAMP_KEYS[role]) || STAMP_KEY;
    localStorage.setItem(key, dataUrl);
  } catch {}
}
export function getApprovalStamp(role) {
  try {
    if (role && ROLE_STAMP_KEYS[role]) {
      const roleStamp = localStorage.getItem(ROLE_STAMP_KEYS[role]);
      if (roleStamp) return roleStamp;
    }
    // Fall back to generic stamp
    return localStorage.getItem(STAMP_KEY) || '';
  } catch { return ''; }
}
export function clearApprovalStamp(role) {
  try {
    if (role && ROLE_STAMP_KEYS[role]) localStorage.removeItem(ROLE_STAMP_KEYS[role]);
    else localStorage.removeItem(STAMP_KEY);
  } catch {}
}
export function getAllRoleStamps() {
  const result = {};
  Object.entries(ROLE_STAMP_KEYS).forEach(([role, key]) => {
    try { result[role] = localStorage.getItem(key) || ''; } catch { result[role] = ''; }
  });
  try { result['generic'] = localStorage.getItem(STAMP_KEY) || ''; } catch {}
  return result;
}

function getBranding() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY_LEGACY);
    if (raw) {
      const t = JSON.parse(raw);
      return {
        name:    t.hotelName ? `${t.hotelName}${t.hotelTagline ? ' — ' + t.hotelTagline : ''}` : 'Grand Memories Resort & Spa',
        address: t.hotelAddress || 'Nakuru, Kenya',
        phone:   t.hotelPhone   || '(+254) 705 225 225',
        email:   t.hotelEmail   || 'info@grandmemories.co.ke',
        pin:     'P051234567X',
        logoUrl: t.logoUrl      || '',
        symbol:  t.hotelSymbol  || '◈',
        primary: t.primaryColor || '#c45e3e',
      };
    }
  } catch {}
  return {
    name:    'Grand Memories Resort & Spa',  // fallback only — overridden by live branding
    address: 'Nakuru, Kenya',
    phone:   '(+254) 705 225 225',
    email:   'info@grandmemories.co.ke',
    pin:     'P051234567X',
    logoUrl: '',
    symbol:  '◈',
    primary: '#c45e3e',
  };
}

const fmtKES = (n) =>
  'KES ' + parseFloat(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const now = () => new Date().toLocaleString('en-KE', { dateStyle: 'long', timeStyle: 'short' });

function buildStyles(primary = '#c8a96e') {
  return `
  <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Source+Sans+3:wght@300;400;600;700&display=swap" rel="stylesheet">
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family: 'Source Sans 3', 'Segoe UI', sans-serif; font-size: 12px; color: #1a1a2e; background: #f0f2f5; print-color-adjust:exact; -webkit-print-color-adjust:exact; }
    /* Header */
    .header { background: #1a1a2e; color: #fff; padding: 18px 28px; display:flex; align-items:center; gap: 20px; margin-bottom: 0; border-bottom: 3px solid ${primary}; }
    .logo-img { max-height: 56px; max-width: 120px; object-fit: contain; flex-shrink:0; }
    .header-text { flex:1; }
    .hotel-name { font-family: 'Playfair Display', serif; font-size: 18px; font-weight: 700; color: #fff; letter-spacing: .5px; }
    .hotel-sub { font-size: 10px; color: rgba(255,255,255,.65); margin-top: 3px; line-height: 1.6; }
    .header-right { text-align: right; font-size: 10px; color: rgba(255,255,255,.6); line-height: 1.7; }
    /* Doc title bar */
    .doc-title { background: #252545; color: #fff; padding: 10px 28px; font-family: 'Playfair Display', serif; font-size: 17px; font-weight: 600; letter-spacing: .3px; text-align:center; }
    .doc-number { background: #2c2c50; color: rgba(255,255,255,.55); padding: 5px 28px; font-size: 10px; text-align:center; letter-spacing:.5px; margin-bottom: 0; }
    /* Content area */
    .content-wrap { padding: 20px 28px; }
    /* KPI cards */
    .kpi-row { display: flex; gap: 12px; margin-bottom: 18px; }
    .kpi { flex:1; background:#fff; border-radius:6px; padding:12px 14px; border-left:4px solid #1a1a2e; box-shadow:0 1px 4px rgba(0,0,0,.08); }
    .kpi .kpi-label { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.8px; color:#888; margin-bottom:4px; }
    .kpi .kpi-value { font-size:16px; font-weight:700; color:#1a1a2e; font-family:'Playfair Display',serif; }
    .kpi.green { border-left-color:#0f9d58; } .kpi.green .kpi-value { color:#0f6b3c; }
    .kpi.red   { border-left-color:#d32f2f; } .kpi.red   .kpi-value { color:#b71c1c; }
    .kpi.blue  { border-left-color:#1565c0; } .kpi.blue  .kpi-value { color:#1565c0; }
    .kpi.gold  { border-left-color:${primary}; } .kpi.gold  .kpi-value { color:#7a5a00; }
    /* Meta grid */
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 5px 24px; margin-bottom: 16px; padding: 12px 14px; background: #fff; border-radius:6px; border-left: 4px solid ${primary}; box-shadow:0 1px 4px rgba(0,0,0,.06); }
    .meta-row { display: flex; gap: 8px; font-size:11px; }
    .meta-label { color: #888; min-width: 95px; font-weight: 600; font-size:10px; text-transform:uppercase; letter-spacing:.5px; }
    .meta-value { color: #1a1a2e; font-weight: 600; }
    /* Cards */
    .card { background:#fff; border-radius:6px; overflow:hidden; margin-bottom:16px; box-shadow:0 1px 4px rgba(0,0,0,.08); }
    .card-header { background:#1a1a2e; color:#fff; padding:9px 14px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1.2px; }
    .card-header.accent { background:#2c3e6b; }
    /* Tables */
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #f7f8fc; }
    th { padding: 8px 14px; text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .8px; color: #555; border-bottom: 2px solid #e0e4f0; }
    th.amount, td.amount { text-align: right; }
    td { padding: 7px 14px; font-size: 12px; border-bottom: 1px solid #f0f2f8; color: #2d2d44; }
    tr:hover td { background: #f7f8fc; }
    tr.section-row td { background: #e8ecf4; font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: .6px; color: #1a1a2e; padding: 6px 14px; }
    tr.subtotal-row td { background: #f0f4ff; font-weight: 600; color: #1a1a2e; }
    tr.total-row td { background: #1a1a2e; color: #fff; font-weight: 700; font-size: 13px; padding: 9px 14px; }
    .amount { text-align: right; font-weight: 600; }
    /* Balance status */
    .balance-status { text-align:center; padding:10px; font-weight:700; font-size:12px; }
    .balance-status.ok  { background:#e8f5e9; color:#2e7d32; }
    .balance-status.err { background:#ffebee; color:#c62828; }
    /* Section title */
    .section-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; color:${primary}; margin: 14px 0 6px; border-bottom:1px solid #e0e4f0; padding-bottom:5px; }
    /* Footer */
    .footer { margin-top: 24px; padding: 10px 14px; background:#fff; border-top: 2px solid #e0e4f0; display:flex; justify-content:space-between; font-size: 10px; color: #aaa; line-height: 1.6; }
    /* Badges */
    .badge { display:inline-block; padding: 2px 9px; border-radius: 12px; font-size:10px; font-weight:700; letter-spacing:.5px; text-transform:uppercase; }
    .badge-paid    { background:#e8f5e9; color:#2e7d32; }
    .badge-pending { background:#fff8e1; color:#f57f17; }
    /* Misc */
    .notice-box  { margin: 12px 0; padding: 10px 14px; border-radius: 4px; font-size: 11px; line-height: 1.6; }
    .notice-request { background:#fff8e1; border:1px dashed #f0a000; color:#7a5000; }
    .notice-paid    { background:#e8f5e9; border:2px solid #0f9d58; color:#1b5e20; }
    .stamp         { display:inline-block; margin-top:10px; padding:4px 14px; border:2px solid #0f9d58; color:#0f9d58; border-radius:3px; font-size:11px; font-weight:700; letter-spacing:2px; text-transform:uppercase; }
    .stamp-pending { border-color:#f57f17; color:#f57f17; }
    .approval-stamp-wrap { position:fixed; bottom:32px; right:32px; z-index:999; opacity:.88; pointer-events:none; }
    .approval-stamp-img  { width:150px; height:150px; object-fit:contain; display:block; }
    .watermark { position:fixed; top:50%; left:50%; transform:translate(-50%,-50%) rotate(-30deg); font-size:80px; color:rgba(26,26,46,0.04); font-weight:900; pointer-events:none; letter-spacing:10px; white-space:nowrap; }
    @media print {
      body { background:#fff; }
      .header,.card-header,.total-row td,.section-row td,.kpi { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
      .approval-stamp-wrap { position:fixed; bottom:24px; right:24px; }
      .watermark { display:block; }
    }
  </style>`;
}

function buildHeader(branding) {
  const logo = branding.logoUrl
    ? `<img src="${branding.logoUrl}" class="logo-img" alt="${branding.name} logo" />`
    : `<div style="font-size:32px;color:rgba(255,255,255,.8);flex-shrink:0">${branding.symbol}</div>`;
  return `
    <div class="header">
      ${logo}
      <div class="header-text">
        <div class="hotel-name">${branding.name}</div>
        <div class="hotel-sub">${branding.address} &nbsp;·&nbsp; ${branding.phone} &nbsp;·&nbsp; ${branding.email}</div>
      </div>
      <div class="header-right">PIN: ${branding.pin}<br>Confidential</div>
    </div>`;
}

// ── Approval Stamp Overlay ─────────────────────────────────────────────────────
// Returns an HTML string that renders the role-appropriate stamp in the
// bottom-right corner of the printed document.
// role: 'storekeeper' | 'accountant' | 'manager' | 'director' | null
function buildApprovedStamp(role) {
  const stampDataUrl = getApprovalStamp(role);
  const roleLabel = (role && ROLE_STAMP_LABELS[role]) || 'APPROVED';

  if (stampDataUrl) {
    return `<div class="approval-stamp-wrap">
      <img src="${stampDataUrl}" class="approval-stamp-img" alt="${roleLabel} Stamp" />
    </div>`;
  }

  // Fallback: built-in SVG stamp with role-specific label
  // Director gets a scalloped (gear-like) border; others get plain circle
  const isDirector = roleLabel === 'DIRECTOR';
  const fontSize   = roleLabel.length > 10 ? '18' : roleLabel.length > 8 ? '20' : '24';
  const scallop    = isDirector
    ? `<path d="M100,6 ${Array.from({length:20},(_,i)=>{
        const a1=(i/20)*Math.PI*2, a2=((i+0.5)/20)*Math.PI*2;
        const r1=94, r2=82;
        return `L${100+r1*Math.sin(a1)},${100-r1*Math.cos(a1)} L${100+r2*Math.sin(a2)},${100-r2*Math.cos(a2)}`;
      }).join(' ')} Z" fill="none" stroke="#8b1a1a" stroke-width="3" opacity="0.85"/>`
    : '';

  return `<div class="approval-stamp-wrap">
    <svg class="approval-stamp-img" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
      ${scallop}
      <circle cx="100" cy="100" r="${isDirector ? 80 : 94}" fill="none" stroke="#8b1a1a" stroke-width="${isDirector ? 3 : 5}" opacity="0.85"/>
      <circle cx="100" cy="100" r="${isDirector ? 70 : 80}" fill="none" stroke="#8b1a1a" stroke-width="2" opacity="0.7"/>
      <path id="topArcR" d="M 22,100 A 78,78 0 0,1 178,100" fill="none"/>
      <path id="botArcR" d="M 32,118 A 78,78 0 0,0 168,118" fill="none"/>
      <text font-family="Arial,sans-serif" font-size="13" font-weight="700" fill="#8b1a1a" letter-spacing="3" text-anchor="middle">
        <textPath href="#topArcR" startOffset="50%">·GRAND MEMORIES·</textPath>
      </text>
      <line x1="28" y1="88" x2="172" y2="88" stroke="#8b1a1a" stroke-width="2" opacity="0.8"/>
      <line x1="28" y1="115" x2="172" y2="115" stroke="#8b1a1a" stroke-width="2" opacity="0.8"/>
      <text x="100" y="110" font-family="Arial,sans-serif" font-size="${fontSize}" font-weight="900" fill="#8b1a1a" text-anchor="middle" letter-spacing="2">${roleLabel}</text>
      <line x1="45" y1="122" x2="72" y2="122" stroke="#8b1a1a" stroke-width="1.2" opacity="0.7"/>
      <text x="100" y="132" font-family="Arial,sans-serif" font-size="10" font-weight="700" fill="#8b1a1a" text-anchor="middle" letter-spacing="2">APPROVED</text>
      <line x1="128" y1="122" x2="155" y2="122" stroke="#8b1a1a" stroke-width="1.2" opacity="0.7"/>
      ${roleLabel === 'DIRECTOR' ? '<text x="85" y="147" font-family="Arial,sans-serif" font-size="13" fill="#8b1a1a">★</text><text x="95" y="147" font-family="Arial,sans-serif" font-size="13" fill="#8b1a1a">★</text><text x="105" y="147" font-family="Arial,sans-serif" font-size="13" fill="#8b1a1a">★</text>' : ''}
      <text font-family="Arial,sans-serif" font-size="11" font-weight="700" fill="#8b1a1a" letter-spacing="2" text-anchor="middle">
        <textPath href="#botArcR" startOffset="50%">★ RESORT &amp; SPA ★</textPath>
      </text>
    </svg>
  </div>`;
}

function openPrint(html, branding, approved, approverRole) {
  const styles = buildStyles(branding?.primary || '#c8a96e');
  const win = window.open('', '_blank', 'width=860,height=960');
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${branding?.name || 'Grand Memories Resort & Spa'}</title>${styles}</head><body>${html}${approved ? buildApprovedStamp(approverRole) : ''}</body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 800);
}

// ── 1. Payment Request Slip (Waiter) ──────────────────────────────────────────
// This is what the waiter prints when a bill is created — it is NOT an official receipt.
// It shows the customer what they owe and asks for payment.
// The cashier issues the official receipt once payment is confirmed.
export function printPaymentRequestSlip(order) {
  const branding = getBranding();
  const items    = order.items || [];

  const thermalStyles = `
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    @page { margin: 4mm; size: 80mm auto; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      color: #000;
      background: #fff;
      max-width: 300px;
      margin: 0 auto;
      padding: 8px 6px;
    }
    .center { text-align: center; }
    .hotel-name { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
    .hotel-sub  { font-size: 10px; color: #444; margin-top: 2px; }
    .logo-img   { max-width: 90%; max-height: 44px; object-fit: contain; margin-bottom: 4px; }
    .divider    { border: none; border-top: 1px dashed #666; margin: 6px 0; }
    .divider-solid { border: none; border-top: 1.5px solid #000; margin: 6px 0; }
    .doc-title  { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; margin: 4px 0 1px; }
    .doc-num    { font-size: 10px; color: #555; margin-bottom: 3px; }
    .field      { display: flex; justify-content: space-between; margin: 3px 0; font-size: 11px; gap: 6px; }
    .fl         { color: #555; flex-shrink: 0; }
    .fv         { font-weight: 700; text-align: right; word-break: break-word; }
    table       { width: 100%; border-collapse: collapse; margin: 6px 0; }
    th          { font-size: 10px; text-align: left; border-bottom: 1px solid #000; padding: 2px 0; text-transform: uppercase; }
    th.r, td.r  { text-align: right; }
    td          { font-size: 11px; padding: 3px 0; border-bottom: 1px dashed #ccc; }
    .total-row td { font-weight: 700; font-size: 13px; border-top: 1.5px solid #000; border-bottom: 1.5px solid #000; padding: 4px 0; }
    .notice     { font-size: 10px; text-align: center; padding: 5px 4px; border: 1px dashed #999; margin: 6px 0; line-height: 1.5; }
    .pending-stamp { display: inline-block; border: 2px solid #000; padding: 3px 12px; font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin: 6px 0; }
    .footer-text { font-size: 10px; color: #444; line-height: 1.6; margin-top: 4px; }
    @media print {
      body { background: #fff; }
    }
  </style>`;

  const logoHtml = branding.logoUrl
    ? `<img src="${branding.logoUrl}" class="logo-img" alt="logo" />`
    : `<div style="font-size:20px;margin-bottom:2px">${branding.symbol}</div>`;

  const rows = items.map(i => `
    <tr>
      <td>${i.name}${i.quantity > 1 ? ` ×${i.quantity}` : ''}</td>
      <td class="r">${fmtKES(i.subtotal || i.price * i.quantity)}</td>
    </tr>`).join('');

  const win = window.open('', '_blank', 'width=360,height=600');
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Payment Request</title>${thermalStyles}</head><body>

  <div class="center">
    ${logoHtml}
    <div class="hotel-name">${branding.name}</div>
    <div class="hotel-sub">${branding.phone}</div>
  </div>

  <hr class="divider-solid"/>

  <div class="center">
    <div class="doc-title">${order.order_type === 'bar' ? 'Bar Bill' : 'Payment Request'}</div>
    <div class="doc-num">Order #${order.id} &nbsp;·&nbsp; ${new Date().toLocaleString('en-KE')}</div>
  </div>

  <hr class="divider"/>

  <div class="field"><span class="fl">Table:</span><span class="fv">${order.table_number || '—'}</span></div>
  <div class="field"><span class="fl">Guest:</span><span class="fv">${order.guest_name || 'Walk-in'}</span></div>
  <div class="field"><span class="fl">Server:</span><span class="fv">${order.waiter_name || '—'}</span></div>
  ${order.kitchen_station ? `<div class="field"><span class="fl">Kitchen:</span><span class="fv">${order.kitchen_station}</span></div>` : ''}

  <hr class="divider"/>

  <table>
    <thead><tr><th>Item</th><th class="r">Total</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr class="total-row"><td>AMOUNT DUE</td><td class="r">${fmtKES(order.total)}</td></tr>
    </tfoot>
  </table>

  ${order.note ? `<div style="font-size:10px;color:#555;margin-top:4px;font-style:italic">Note: ${order.note}</div>` : ''}

  <div class="notice">
    ⚠ PAYMENT REQUEST — NOT A RECEIPT<br>
    Official receipt issued by cashier on payment
  </div>

  <div class="center">
    <div class="pending-stamp">PAYMENT PENDING</div>
  </div>

  <hr class="divider"/>

  <div class="center footer-text">
    ${branding.name}<br>
    Thank you for dining with us
  </div>

</body></html>`);
  win.document.close();
  setTimeout(() => win.print(), 500);
}

// ── 2. Official Cashier Receipt ───────────────────────────────────────────────
// Issued ONLY by the cashier after confirming payment.
export function printCashierReceipt(bill) {
  const branding = getBranding();
  const isApproved = false;
  const items    = bill.order_items || [];
  const rows     = items.map(i => `
    <tr>
      <td>${i.name}</td>
      <td style="text-align:center">${i.quantity}</td>
      <td class="amount">${fmtKES(i.price)}</td>
      <td class="amount">${fmtKES(i.subtotal || parseFloat(i.price) * i.quantity)}</td>
    </tr>`).join('');

  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Official ${bill.bill_type === 'Bar' ? 'Bar' : 'Restaurant'} Receipt</div>
    <div class="doc-number">Receipt #${bill.id} &nbsp;|&nbsp; ${now()}</div>

    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Guest:</span><span class="meta-value">${bill.guest_name || 'Walk-in'}</span></div>
      <div class="meta-row"><span class="meta-label">Table / Seat:</span><span class="meta-value">${bill.order_table || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Server:</span><span class="meta-value">${bill.waiter_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Payment Mode:</span><span class="meta-value">${(bill.payment_mode||'').toUpperCase()}</span></div>
      ${bill.transaction_code ? `<div class="meta-row"><span class="meta-label">Tx Code:</span><span class="meta-value" style="font-family:monospace;color:#2ecc71;font-weight:700">${bill.transaction_code}</span></div>` : ''}
      ${bill.confirmed_by_name ? `<div class="meta-row"><span class="meta-label">Confirmed By:</span><span class="meta-value">${bill.confirmed_by_name}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Date & Time:</span><span class="meta-value">${now()}</span></div>
    </div>

    <table>
      <thead><tr><th>Item</th><th style="text-align:center">Qty</th><th class="amount">Unit Price</th><th class="amount">Amount</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr class="total-row"><td colspan="3">TOTAL PAID</td><td class="amount">${fmtKES(bill.total)}</td></tr>
      </tfoot>
    </table>

    <div style="text-align:center;margin-top:14px">
      <span class="stamp">✓ PAID — OFFICIAL RECEIPT</span>
    </div>

    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.email} &nbsp;·&nbsp; ${branding.phone}<br>
      PIN: ${branding.pin} &nbsp;·&nbsp; ${branding.address}<br>
      This is a computer-generated official receipt<br>
      <em>Thank you for dining at ${branding.name}</em>
    </div>`, branding, isApproved);
}

// Keep old name as alias for backward compatibility
export const printBillReceiptWaiter = printPaymentRequestSlip;

// ── 3. Food / Bar Bill (generic) ─────────────────────────────────────────────
export function printBillReceipt(bill) {
  // Delegates to printCashierReceipt or printPaymentRequestSlip — both use getBranding()
  const isPaid = bill.status === 'paid';
  if (isPaid) return printCashierReceipt(bill);
  return printPaymentRequestSlip({
    id: bill.id,
    table_number: bill.order_table,
    guest_name: bill.guest_name,
    waiter_name: bill.waiter_name,
    order_type: bill.bill_type === 'Bar' ? 'bar' : 'food',
    items: (bill.order_items || []).map(i => ({ ...i, subtotal: parseFloat(i.price) * i.quantity })),
    total: bill.total,
  });
}

// ── 4. Room Invoice ──────────────────────────────────────────────────────────
export function printRoomInvoice(guest) {
  const branding = getBranding();
  const isApproved = false;
  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Room Invoice</div>
    <div class="doc-number">Guest #${guest.id} &nbsp;|&nbsp; Printed: ${now()}</div>

    <div class="section-title">Guest Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Name:</span><span class="meta-value">${guest.name}</span></div>
      <div class="meta-row"><span class="meta-label">ID/Passport:</span><span class="meta-value">${guest.id_number}</span></div>
      <div class="meta-row"><span class="meta-label">Phone:</span><span class="meta-value">${guest.phone}</span></div>
      ${guest.email ? `<div class="meta-row"><span class="meta-label">Email:</span><span class="meta-value">${guest.email}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Adults:</span><span class="meta-value">${guest.adults}</span></div>
      <div class="meta-row"><span class="meta-label">Children:</span><span class="meta-value">${guest.children}</span></div>
    </div>

    <div class="section-title">Accommodation Details</div>
    <table>
      <thead><tr><th>Description</th><th>Room</th><th>Type</th><th style="text-align:center">Nights</th><th class="amount">Rate/Night</th><th class="amount">Total</th></tr></thead>
      <tbody>
        <tr>
          <td>Room Accommodation</td>
          <td>Room ${guest.room_number || '—'}</td>
          <td>${guest.room_type || '—'}</td>
          <td style="text-align:center">${guest.nights}</td>
          <td class="amount">${fmtKES(parseFloat(guest.room_charge) / guest.nights)}</td>
          <td class="amount">${fmtKES(guest.room_charge)}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="total-row"><td colspan="5">TOTAL AMOUNT</td><td class="amount">${fmtKES(guest.room_charge)}</td></tr>
      </tfoot>
    </table>

    <div class="meta-grid" style="margin-top:10px">
      <div class="meta-row"><span class="meta-label">Check-In:</span><span class="meta-value">${guest.check_in}</span></div>
      <div class="meta-row"><span class="meta-label">Check-Out:</span><span class="meta-value">${guest.check_out}</span></div>
      <div class="meta-row"><span class="meta-label">Payment Mode:</span><span class="meta-value">${(guest.payment_mode||'').toUpperCase()}</span></div>
      <div class="meta-row"><span class="meta-label">Status:</span><span class="meta-value">${guest.status?.toUpperCase()}</span></div>
    </div>

    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.address}<br>
      ${branding.email} &nbsp;·&nbsp; ${branding.phone} &nbsp;·&nbsp; PIN: ${branding.pin}<br>
      This is a computer-generated invoice
    </div>`, branding, isApproved);
}

// ── 5. Reservation Confirmation ───────────────────────────────────────────────
export function printReservationConfirmation(res) {
  const branding = getBranding();
  const isApproved = false;
  const depositAmt = res.deposit_amount ? parseFloat(res.deposit_amount) : Math.round(parseFloat(res.estimated_total || 0) * 0.3);
  const hasTransport = res.needs_transport;
  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Reservation Confirmation</div>
    <div class="doc-number">Ref: <strong>${res.confirmation_code}</strong> &nbsp;|&nbsp; Printed: ${now()}</div>

    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Guest Name:</span><span class="meta-value">${res.name}</span></div>
      <div class="meta-row"><span class="meta-label">Email:</span><span class="meta-value">${res.email}</span></div>
      <div class="meta-row"><span class="meta-label">Phone:</span><span class="meta-value">${res.phone}</span></div>
      ${res.id_number ? `<div class="meta-row"><span class="meta-label">ID/Passport:</span><span class="meta-value">${res.id_number}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Room Type:</span><span class="meta-value">${res.room_type}</span></div>
      <div class="meta-row"><span class="meta-label">Check-In:</span><span class="meta-value">${res.check_in}</span></div>
      <div class="meta-row"><span class="meta-label">Check-Out:</span><span class="meta-value">${res.check_out}</span></div>
      <div class="meta-row"><span class="meta-label">Nights:</span><span class="meta-value">${res.nights}</span></div>
      <div class="meta-row"><span class="meta-label">Guests:</span><span class="meta-value">${res.adults} Adult(s) &nbsp;·&nbsp; ${res.children} Child(ren)</span></div>
      <div class="meta-row"><span class="meta-label">Est. Room Total:</span><span class="meta-value" style="color:#8b6914;font-weight:700">${fmtKES(res.estimated_total)}</span></div>
      ${hasTransport ? `<div class="meta-row"><span class="meta-label">Airport Transport:</span><span class="meta-value" style="color:#8b6914">${fmtKES(res.transport_fee || 0)} — ${res.transport_pickup || 'Airport'}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Payment Mode:</span><span class="meta-value">${(res.payment_mode || 'cash').toUpperCase()}</span></div>
      <div class="meta-row"><span class="meta-label">Status:</span><span class="meta-value" style="color:${res.status==='confirmed'?'#2ecc71':'#e67e22'};font-weight:700">${res.status?.toUpperCase()}</span></div>
    </div>

    ${res.special_requests ? `<div class="section-title">Special Requests</div><p style="font-size:12px;color:#555;padding:6px 0;margin:0">${res.special_requests}</p>` : ''}

    <!-- Deposit / Payment Notice -->
    <div style="margin-top:14px;padding:12px 14px;background:#fff8e8;border:2px solid #c8a96e;border-radius:4px">
      <div style="font-size:11px;font-weight:800;color:#7a5c10;letter-spacing:1px;text-transform:uppercase;margin-bottom:6px">⚠ Reservation Deposit Required</div>
      <div style="font-size:12px;color:#333;line-height:1.7">
        To <strong>confirm and secure</strong> your reservation, a non-refundable deposit of
        <strong style="color:#8b6914;font-size:14px">${fmtKES(depositAmt)}</strong> is required
        before <strong>${res.check_in}</strong>.<br>
        <span style="font-size:11px;color:#666">Payment can be made via M-Pesa, bank transfer, or at the front desk.
        The remaining balance of <strong>${fmtKES(Math.max(0, parseFloat(res.estimated_total||0) - depositAmt))}</strong> is due at check-in.
        Failure to pay the deposit within 48 hours may result in automatic cancellation.</span>
      </div>
      <div style="margin-top:8px;font-size:11px;color:#555">
        <strong>Pay to:</strong> ${branding.name} &nbsp;·&nbsp; ${branding.phone}${branding.pin ? ` &nbsp;·&nbsp; KRA PIN: ${branding.pin}` : ''}
      </div>
    </div>

    ${hasTransport ? `
    <div style="margin-top:10px;padding:10px 12px;background:#f0f8ff;border:1px solid #3498db44;border-radius:4px">
      <div style="font-size:10px;font-weight:700;color:#1a5276;letter-spacing:1px;text-transform:uppercase;margin-bottom:4px">🚐 Airport Transfer Included</div>
      <div style="font-size:12px;color:#333">Pickup from: <strong>${res.transport_pickup || 'Airport'}</strong> &nbsp;·&nbsp; Fee: <strong style="color:#8b6914">${fmtKES(res.transport_fee || 0)}</strong> (paid upfront)</div>
      <div style="font-size:11px;color:#666;margin-top:3px">Please contact us 24 hours before arrival to confirm your flight details.</div>
    </div>` : ''}

    <div style="text-align:center;margin-top:14px;padding:12px;background:#f8f6f0;border:1px solid #c8a96e44;border-radius:4px">
      <div style="font-size:9px;color:#888;letter-spacing:3px;text-transform:uppercase;margin-bottom:6px">Confirmation Code</div>
      <div style="font-size:30px;font-weight:800;color:#8b6914;letter-spacing:8px;font-family:monospace">${res.confirmation_code}</div>
      <div style="font-size:10px;color:#aaa;margin-top:4px">Present this code at check-in. Keep this document for your records.</div>
    </div>

    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.address}<br>
      Tel: ${branding.phone} &nbsp;·&nbsp; Email: ${branding.email}${branding.pin ? ` &nbsp;·&nbsp; KRA PIN: ${branding.pin}` : ''}<br>
      This is a computer-generated document — valid without signature
    </div>`, branding, isApproved);
}

// ── 6. Conference Booking Invoice ─────────────────────────────────────────────
export function printConferenceInvoice(booking) {
  const branding = getBranding();
  const isApproved = false;
  const extras = [];
  if (booking.needs_projector)  extras.push({ name: 'Projector', amount: 2000 });
  if (booking.needs_microphone) extras.push({ name: 'Microphone / PA System', amount: 1500 });
  if (booking.needs_catering)   extras.push({ name: `Catering (${booking.attendees} pax × KES 500)`, amount: 500 * booking.attendees });
  const extraRows = extras.map(e => `<tr><td>${e.name}</td><td></td><td class="amount">${fmtKES(e.amount)}</td></tr>`).join('');
  const sessionMap = { half_day_am: 'Half Day AM (8am–1pm)', half_day_pm: 'Half Day PM (1pm–6pm)', full_day: 'Full Day (8am–6pm)' };

  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Conference Booking ${booking.status === 'completed' ? 'Receipt' : 'Invoice'}</div>
    <div class="doc-number">Ref: <strong>${booking.confirmation_code}</strong> &nbsp;|&nbsp; Printed: ${now()}</div>

    <div class="section-title">Client Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Organiser:</span><span class="meta-value">${booking.organiser_name}</span></div>
      <div class="meta-row"><span class="meta-label">Organisation:</span><span class="meta-value">${booking.organisation || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Phone:</span><span class="meta-value">${booking.organiser_phone}</span></div>
      <div class="meta-row"><span class="meta-label">Email:</span><span class="meta-value">${booking.organiser_email}</span></div>
    </div>

    <div class="section-title">Event Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Event:</span><span class="meta-value">${booking.event_name}</span></div>
      <div class="meta-row"><span class="meta-label">Date:</span><span class="meta-value">${booking.event_date}</span></div>
      <div class="meta-row"><span class="meta-label">Venue:</span><span class="meta-value">${booking.conference_room_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Session:</span><span class="meta-value">${sessionMap[booking.session_type] || booking.session_type}</span></div>
      <div class="meta-row"><span class="meta-label">Attendees:</span><span class="meta-value">${booking.attendees} persons</span></div>
    </div>

    <div class="section-title">Charges</div>
    <table>
      <thead><tr><th>Description</th><th></th><th class="amount">Amount</th></tr></thead>
      <tbody>
        <tr><td>Venue: ${booking.conference_room_name} (${sessionMap[booking.session_type] || booking.session_type})</td><td></td><td class="amount">${fmtKES(booking.room_rate)}</td></tr>
        ${extraRows}
      </tbody>
      <tfoot>
        ${extras.length ? `<tr class="subtotal-row"><td colspan="2">Sub-total (Extras)</td><td class="amount">${fmtKES(booking.extras_charge)}</td></tr>` : ''}
        <tr class="total-row"><td colspan="2">TOTAL</td><td class="amount">${fmtKES(booking.total)}</td></tr>
      </tfoot>
    </table>

    <div class="meta-grid" style="margin-top:10px">
      <div class="meta-row"><span class="meta-label">Payment Mode:</span><span class="meta-value">${(booking.payment_mode||'').toUpperCase()}</span></div>
      ${booking.transaction_code ? `<div class="meta-row"><span class="meta-label">Tx Ref:</span><span class="meta-value">${booking.transaction_code}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Status:</span><span class="meta-value">${booking.status?.toUpperCase()}</span></div>
    </div>
    ${booking.status === 'completed' ? `<div style="text-align:center;margin-top:10px"><span class="stamp">✓ PAID</span></div>` : ''}
    ${booking.special_requests ? `<div class="section-title">Special Requests</div><p style="font-size:12px;color:#555;padding:6px 0">${booking.special_requests}</p>` : ''}
    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.address}<br>
      ${branding.email} &nbsp;·&nbsp; ${branding.phone} &nbsp;·&nbsp; PIN: ${branding.pin}<br>
      This is a computer-generated document
    </div>`, branding, isApproved);
}

// ── 6b. Transport Receipt ─────────────────────────────────────────────────────
export function printTransportReceipt(tr) {
  const branding = getBranding();
  const isApproved = false;
  const statusColor = tr.status === 'completed' ? '#2ecc71' : tr.status === 'confirmed' ? '#3498db' : '#e67e22';
  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Transport Service Receipt</div>
    <div class="doc-number">Ref: <strong>TR-${tr.id}</strong> &nbsp;|&nbsp; Printed: ${now()}</div>

    <div class="section-title">Guest Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Name:</span><span class="meta-value">${tr.requester_name}</span></div>
      <div class="meta-row"><span class="meta-label">Phone:</span><span class="meta-value">${tr.requester_phone}</span></div>
      <div class="meta-row"><span class="meta-label">Passengers:</span><span class="meta-value">${tr.passengers}</span></div>
    </div>

    <div class="section-title">Trip Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Pickup From:</span><span class="meta-value">${tr.pickup_location}</span></div>
      <div class="meta-row"><span class="meta-label">Drop-Off To:</span><span class="meta-value">${tr.dropoff_location}</span></div>
      <div class="meta-row"><span class="meta-label">Date & Time:</span><span class="meta-value">${new Date(tr.pickup_datetime).toLocaleString('en-KE')}</span></div>
      <div class="meta-row"><span class="meta-label">Vehicle:</span><span class="meta-value">${({saloon:'Saloon Car',suv:'SUV / 4x4',minivan:'Mini Van',shuttle:'Shuttle Bus'})[tr.vehicle_type] || tr.vehicle_type}</span></div>
    </div>

    <div class="section-title">Payment</div>
    <table>
      <thead><tr><th>Description</th><th class="amount">Amount</th></tr></thead>
      <tbody>
        <tr><td>Airport/Transport Transfer — ${tr.pickup_location} → ${tr.dropoff_location}</td><td class="amount">${fmtKES(tr.fee)}</td></tr>
      </tbody>
      <tfoot>
        <tr class="total-row"><td>TOTAL PAID</td><td class="amount">${fmtKES(tr.fee)}</td></tr>
      </tfoot>
    </table>

    <div class="meta-grid" style="margin-top:10px">
      <div class="meta-row"><span class="meta-label">Payment Mode:</span><span class="meta-value">${(tr.payment_mode||'').toUpperCase()}</span></div>
      ${tr.transaction_code ? `<div class="meta-row"><span class="meta-label">Transaction Ref:</span><span class="meta-value">${tr.transaction_code}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Status:</span><span class="meta-value" style="color:${statusColor};font-weight:700">${(tr.status||'').toUpperCase()}</span></div>
    </div>
    ${tr.status === 'completed' ? '<div style="text-align:center;margin-top:10px"><span class="stamp">✓ PAID</span></div>' : ''}
    ${tr.notes ? `<div class="section-title">Notes</div><p style="font-size:12px;color:#555;padding:6px 0">${tr.notes}</p>` : ''}

    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.address}<br>
      Tel: ${branding.phone} &nbsp;·&nbsp; Email: ${branding.email}${branding.pin ? ` &nbsp;·&nbsp; KRA PIN: ${branding.pin}` : ''}<br>
      Thank you for choosing our transport service
    </div>`, branding, isApproved);
}

// ── 7. Payment Receipt (generic) ─────────────────────────────────────────────
export function printPaymentReceipt(payment) {
  const branding = getBranding();
  const isApproved = false;
  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Payment Receipt</div>
    <div class="doc-number">Receipt #${payment.id} &nbsp;|&nbsp; Printed: ${now()}</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Guest/Client:</span><span class="meta-value">${payment.guest_name}</span></div>
      <div class="meta-row"><span class="meta-label">Type:</span><span class="meta-value">${payment.payment_type}</span></div>
      <div class="meta-row"><span class="meta-label">Payment Mode:</span><span class="meta-value">${(payment.payment_mode||'').toUpperCase()}</span></div>
      ${payment.transaction_code ? `<div class="meta-row"><span class="meta-label">Tx Code:</span><span class="meta-value">${payment.transaction_code}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Confirmed By:</span><span class="meta-value">${payment.confirmed_by_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Date & Time:</span><span class="meta-value">${new Date(payment.paid_at).toLocaleString('en-KE')}</span></div>
    </div>
    <table>
      <thead><tr><th>Description</th><th class="amount">Amount</th></tr></thead>
      <tbody><tr><td>${payment.description || payment.payment_type}</td><td class="amount">${fmtKES(payment.amount)}</td></tr></tbody>
      <tfoot><tr class="total-row"><td>TOTAL PAID</td><td class="amount">${fmtKES(payment.amount)}</td></tr></tfoot>
    </table>
    <div style="text-align:center;margin-top:10px"><span class="stamp">✓ PAID</span></div>
    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.email} &nbsp;·&nbsp; ${branding.phone}<br>
      This is a computer-generated official receipt
    </div>`, branding);
}

// ── 8. Gate Visitor Pass ──────────────────────────────────────────────────────
// ── QR code generator for print (canvas-based, no external lib) ─────────────
function _generateQRDataURL(text, size) {
  size = size || 180;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    function hashBits(str, count) {
      let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
      for (let i = 0; i < str.length; i++) {
        const c = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ c, 2654435761);
        h2 = Math.imul(h2 ^ c, 1597334677);
      }
      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
      const combined = (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(2).padStart(64, "0");
      let bits = "";
      while (bits.length < count) bits += combined;
      return bits.slice(0, count);
    }
    const modules = 29;
    const cellSize = Math.floor((size - 8) / (modules + 2));
    const offset = Math.floor((size - cellSize * modules) / 2);
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, size, size);
    const bits = hashBits(text, modules * modules);
    ctx.fillStyle = "#000";
    for (let row = 0; row < modules; row++) {
      for (let col = 0; col < modules; col++) {
        if (bits[row * modules + col] === "1") {
          ctx.fillRect(offset + col * cellSize, offset + row * cellSize, cellSize - 1, cellSize - 1);
        }
      }
    }
    const df = function(x, y) {
      ctx.fillStyle = "#000";
      ctx.fillRect(offset + x*cellSize, offset + y*cellSize, cellSize*7, cellSize*7);
      ctx.fillStyle = "#fff";
      ctx.fillRect(offset+(x+1)*cellSize, offset+(y+1)*cellSize, cellSize*5, cellSize*5);
      ctx.fillStyle = "#000";
      ctx.fillRect(offset+(x+2)*cellSize, offset+(y+2)*cellSize, cellSize*3, cellSize*3);
    };
    df(0,0); df(modules-7,0); df(0,modules-7);
    return canvas.toDataURL("image/png");
  } catch(e) { return ""; }
}

export function printGatePass(visitor) {
  const branding = getBranding();
  const typeMap = {
    guest:"Hotel Guest", visitor:"Visitor", conference:"Conference Attendee",
    delivery:"Delivery / Supplier", staff:"Staff", other:"Other",
  };

  const scanURL = window.location.origin + "/gate-scan/" + (visitor.qr_token || "");
  const qrDataURL = visitor.qr_token ? _generateQRDataURL(scanURL, 160) : "";

  const thermalStyles = `
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    @page { margin: 10mm; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 12px;
      color: #000;
      background: #fff;
      max-width: 340px;
      margin: 0 auto;
      padding: 12px 10px;
    }
    .center { text-align: center; }
    .hotel-name { font-size: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
    .hotel-sub { font-size: 10px; color: #444; margin-top: 2px; }
    .logo-img { max-width: 100%; max-height: 50px; object-fit: contain; margin-bottom: 6px; }
    .divider { border: none; border-top: 1px dashed #999; margin: 8px 0; }
    .divider-solid { border: none; border-top: 1.5px solid #000; margin: 8px 0; }
    .pass-title { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; margin: 6px 0 2px; }
    .pass-no { font-size: 10px; color: #666; margin-bottom: 4px; }
    .visitor-name { font-size: 18px; font-weight: 700; text-transform: uppercase; margin: 6px 0 2px; }
    .visitor-type { font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: #555; margin-bottom: 6px; }
    .field { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; gap: 8px; }
    .field-label { color: #666; flex-shrink: 0; }
    .field-value { font-weight: 700; text-align: right; word-break: break-word; }
    .plate { font-size: 15px; font-weight: 900; letter-spacing: 4px; border: 2px solid #000; display: inline-block; padding: 3px 10px; margin: 4px 0; border-radius: 2px; }
    .qr-wrap { margin: 10px auto 4px; display: flex; flex-direction: column; align-items: center; }
    .qr-wrap img { border: 2px solid #000; padding: 5px; background: #fff; }
    .qr-label { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; margin-top: 4px; color: #555; }
    .footer-text { font-size: 9px; color: #555; line-height: 1.7; margin-top: 6px; }
  </style>`;

  const logoHtml = branding.logoUrl
    ? `<img src="${branding.logoUrl}" class="logo-img" alt="logo" />`
    : `<div style="font-size:22px;margin-bottom:2px">${branding.symbol}</div>`;

  const win = window.open('', '_blank', 'width=400,height=700');
  if (!win) { alert('Pop-up blocked — please allow pop-ups for this site and try again.'); return; }

  const bodyHtml = `
    <div class="center">
      ${logoHtml}
      <div class="hotel-name">${branding.name}</div>
      <div class="hotel-sub">${branding.address} · ${branding.phone}</div>
    </div>

    <hr class="divider-solid" />

    <div class="center">
      <div class="pass-title">Visitor Entry Pass</div>
      <div class="pass-no">Pass #${visitor.id} · ${new Date(visitor.time_in).toLocaleDateString("en-KE")}</div>
      <div class="visitor-name">${visitor.name}</div>
      <div class="visitor-type">${typeMap[visitor.visitor_type] || visitor.visitor_type}</div>
    </div>

    <hr class="divider" />

    <div class="field"><span class="field-label">ID / Passport:</span><span class="field-value">${visitor.id_number}</span></div>
    ${visitor.phone ? `<div class="field"><span class="field-label">Phone:</span><span class="field-value">${visitor.phone}</span></div>` : ""}
    ${visitor.purpose ? `<div class="field"><span class="field-label">Purpose:</span><span class="field-value">${visitor.purpose}</span></div>` : ""}
    ${visitor.host_name ? `<div class="field"><span class="field-label">Host / Dept:</span><span class="field-value">${visitor.host_name}</span></div>` : ""}
    <div class="field"><span class="field-label">Time In:</span><span class="field-value">${new Date(visitor.time_in).toLocaleString("en-KE")}</span></div>
    <div class="field"><span class="field-label">Logged By:</span><span class="field-value">${visitor.logged_by_name || "—"}</span></div>

    ${visitor.car_plate ? `
    <hr class="divider" />
    <div class="center">
      <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#555;margin-bottom:2px">Vehicle</div>
      <div class="plate">${visitor.car_plate}</div>
    </div>` : ""}

    ${qrDataURL ? `
    <hr class="divider" />
    <div class="qr-wrap">
      <img src="${qrDataURL}" width="140" height="140" alt="QR Code" />
      <div class="qr-label">Scan at exit gate</div>
    </div>` : ""}

    <hr class="divider-solid" />

    <div class="center footer-text">
      ${branding.name} · Security &amp; Guest Relations<br>
      Valid for date of issue only
    </div>`;

  // Write the full document — inline auto-print script avoids the reload/blank-page
  // race condition caused by calling win.print() from the opener after document.close().
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Gate Pass</title>${thermalStyles}
  <script>
    function _autoPrint() {
      // Wait for all images (logo, QR) to finish loading before printing
      var imgs = document.images;
      var total = imgs.length;
      if (total === 0) { window.print(); return; }
      var loaded = 0;
      var done = false;
      function tryPrint() {
        if (done) return;
        done = true;
        window.print();
      }
      for (var i = 0; i < total; i++) {
        if (imgs[i].complete) { loaded++; if (loaded === total) { tryPrint(); } }
        else {
          imgs[i].onload  = function() { loaded++; if (loaded === total) tryPrint(); };
          imgs[i].onerror = function() { loaded++; if (loaded === total) tryPrint(); };
        }
      }
      // Safety fallback — print after 2 s even if images stall
      setTimeout(tryPrint, 2000);
    }
  <\/script>
  </head><body onload="_autoPrint()">
  ${bodyHtml}
  </body></html>`);
  win.document.close();
}

// ── 9. Guest Folio / Full Bill ─────────────────────────────────────────────────
export function printGuestFolio(folio) {
  const branding = getBranding();
  const isApproved = false;
  const { guest, charges, room_charge, folio_balance, total_bill } = folio;
  const TYPE_ICONS = { room:'🛏️', food:'🍽️', bar:'🍹', pool:'🏊', laundry:'👔', spa:'💆', minibar:'🧴', telephone:'📞', conference:'🏛️', other:'📌' };

  const chargeRows = (charges || []).map(c => `
    <tr>
      <td>${new Date(c.created_at).toLocaleDateString('en-KE')}</td>
      <td>${TYPE_ICONS[c.charge_type] || ''} ${c.description}</td>
      <td style="text-align:center">${c.quantity > 1 ? c.quantity : '—'}</td>
      <td class="amount">${fmtKES(c.amount)}</td>
      <td style="text-align:center">${c.settled ? '<span style="color:#2ecc71;font-size:11px">✓ Settled</span>' : '<span style="color:#e67e22;font-size:11px">Pending</span>'}</td>
    </tr>`).join('');

  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Guest Folio & Final Bill</div>
    <div class="doc-number">Guest #${guest.id} &nbsp;|&nbsp; Printed: ${now()}</div>

    <div class="section-title">Guest Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Name:</span><span class="meta-value">${guest.name}</span></div>
      <div class="meta-row"><span class="meta-label">ID/Passport:</span><span class="meta-value">${guest.id_number}</span></div>
      <div class="meta-row"><span class="meta-label">Room:</span><span class="meta-value">Room ${guest.room_number || '—'} (${guest.room_type || '—'})</span></div>
      <div class="meta-row"><span class="meta-label">Phone:</span><span class="meta-value">${guest.phone}</span></div>
      <div class="meta-row"><span class="meta-label">Check-In:</span><span class="meta-value">${guest.check_in}</span></div>
      <div class="meta-row"><span class="meta-label">Check-Out:</span><span class="meta-value">${guest.check_out}</span></div>
    </div>

    <div class="section-title">Charges Breakdown</div>
    <table>
      <thead><tr><th>Date</th><th>Description</th><th style="text-align:center">Qty</th><th class="amount">Amount</th><th style="text-align:center">Status</th></tr></thead>
      <tbody>
        <tr style="background:#f8f6f0">
          <td>${guest.check_in}</td>
          <td>🛏️ Room Accommodation — ${guest.room_type} × ${guest.nights} night${guest.nights !== 1 ? 's' : ''}</td>
          <td style="text-align:center">${guest.nights}</td>
          <td class="amount">${fmtKES(room_charge)}</td>
          <td style="text-align:center"><span style="color:#2ecc71;font-size:11px">✓ Paid</span></td>
        </tr>
        ${chargeRows}
      </tbody>
      <tfoot>
        <tr class="subtotal-row"><td colspan="3">Room Charges</td><td class="amount">${fmtKES(room_charge)}</td><td></td></tr>
        <tr class="subtotal-row"><td colspan="3">Incidentals (F&B, Pool, etc.)</td><td class="amount">${fmtKES(folio_balance)}</td><td></td></tr>
        <tr class="total-row"><td colspan="3">TOTAL BILL</td><td class="amount">${fmtKES(total_bill)}</td><td></td></tr>
      </tfoot>
    </table>

    <div class="footer">
      Thank you for staying at ${branding.name}<br>
      ${branding.email} &nbsp;·&nbsp; ${branding.phone} &nbsp;·&nbsp; PIN: ${branding.pin}<br>
      ${branding.address}<br>
      This is a computer-generated folio
    </div>`, branding, isApproved);
}

// ── 10. Checkout Invoice ───────────────────────────────────────────────────────
export function printCheckoutInvoice(data) {
  const branding = getBranding();
  const isApproved = false;
  const { guest, folio_charges, room_charge, grand_total, payment_mode } = data;
  const charges = folio_charges || [];
  const isCash  = (payment_mode || '').toLowerCase() === 'cash';
  const byType  = {};
  charges.forEach(c => {
    const t = c.charge_type || 'other';
    if (!byType[t]) byType[t] = { label: t, items: [], subtotal: 0 };
    byType[t].items.push(c);
    byType[t].subtotal += parseFloat(c.amount);
  });
  const typeLabels = { room:'Room Rate', food:'Restaurant', bar:'Bar', pool:'Swimming Pool', laundry:'Laundry', spa:'Spa & Wellness', minibar:'Mini Bar', conference:'Conference', gift_shop:'Gift Shop', telephone:'Telephone', other:'Other' };
  const chargesRows = Object.values(byType).map(grp => `
    <tr><td colspan="3" style="background:#1a1a1a;color:#c8a96e;font-weight:700;padding:5px 8px;font-size:10px;letter-spacing:1px;text-transform:uppercase;">${typeLabels[grp.label]||grp.label}</td></tr>
    ${grp.items.map(c => `<tr><td>${c.description}</td><td style="text-align:center">${c.quantity||1}</td><td class="amount">${fmtKES(c.amount)}</td></tr>`).join('')}
    <tr class="subtotal-row"><td colspan="2" style="text-align:right;font-weight:600;color:#aaa;padding-right:10px;">Subtotal</td><td class="amount">${fmtKES(grp.subtotal)}</td></tr>
  `).join('');

  const VAT_RATE  = 0.16;
  const vatAmount = grand_total * VAT_RATE / (1 + VAT_RATE);
  const netAmount = grand_total - vatAmount;

  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">${isCash ? 'Tax Receipt & Invoice' : 'Checkout Invoice'}</div>
    <div class="doc-number">Guest #${guest.id} &nbsp;|&nbsp; Checkout: ${new Date().toLocaleDateString('en-KE')} &nbsp;|&nbsp; Printed: ${now()}</div>

    <div class="section-title">Guest Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Name:</span><span class="meta-value">${guest.name}</span></div>
      <div class="meta-row"><span class="meta-label">ID/Passport:</span><span class="meta-value">${guest.id_number}</span></div>
      <div class="meta-row"><span class="meta-label">Room:</span><span class="meta-value">Room ${guest.room_number||'—'} (${guest.room_type||'—'})</span></div>
      <div class="meta-row"><span class="meta-label">Check-In:</span><span class="meta-value">${guest.check_in}</span></div>
      <div class="meta-row"><span class="meta-label">Check-Out:</span><span class="meta-value">${new Date().toLocaleDateString('en-KE')}</span></div>
      <div class="meta-row"><span class="meta-label">Nights:</span><span class="meta-value">${guest.nights}</span></div>
      <div class="meta-row"><span class="meta-label">Payment:</span><span class="meta-value">${(payment_mode||'').toUpperCase()}</span></div>
    </div>

    <div class="section-title">Bill Summary</div>
    <table>
      <thead><tr><th>Description</th><th style="text-align:center">Qty</th><th class="amount">Amount (KES)</th></tr></thead>
      <tbody>
        <tr><td colspan="3" style="background:#1a1a1a;color:#c8a96e;font-weight:700;padding:5px 8px;font-size:10px;letter-spacing:1px;text-transform:uppercase;">ACCOMMODATION</td></tr>
        <tr><td>Room ${guest.room_number||'—'} — ${guest.room_type||'—'} (${guest.nights} night${guest.nights>1?'s':''})</td><td style="text-align:center">${guest.nights}</td><td class="amount">${fmtKES(room_charge)}</td></tr>
        ${chargesRows}
      </tbody>
      <tfoot>
        <tr><td colspan="2" style="text-align:right;color:#aaa;padding-right:10px;">Net Amount (ex-VAT):</td><td class="amount">${fmtKES(netAmount)}</td></tr>
        <tr><td colspan="2" style="text-align:right;color:#aaa;padding-right:10px;">VAT @ 16% (KRA):</td><td class="amount">${fmtKES(vatAmount)}</td></tr>
        <tr class="total-row"><td colspan="2">TOTAL AMOUNT DUE</td><td class="amount">${fmtKES(grand_total)}</td></tr>
      </tfoot>
    </table>

    ${isCash ? `<div style="margin:14px 0;padding:12px 14px;border:2px solid #2ecc71;border-radius:4px;background:#0a180a;"><div style="font-size:11px;color:#2ecc71;letter-spacing:2px;text-transform:uppercase;margin-bottom:4px;">✓ PAYMENT RECEIVED</div><div style="font-size:15px;font-weight:700;color:#2ecc71;">Cash Payment: ${fmtKES(grand_total)}</div></div>` : ''}

    <div class="footer">
      Thank you for staying at ${branding.name}<br>
      ${branding.email} &nbsp;·&nbsp; ${branding.phone} &nbsp;·&nbsp; PIN: ${branding.pin}<br>
      ${branding.address}<br>
      ${isCash ? 'This is your official cash receipt and tax invoice' : 'This is a computer-generated invoice · Not a receipt until payment is made'}
    </div>`, branding, isApproved);
}

// ── 11. Gift Shop Receipt ──────────────────────────────────────────────────────
export function printGiftShopReceipt(sale) {
  const branding = getBranding();
  const isApproved = false;
  const isCash   = (sale.payment_mode||'').toLowerCase() === 'cash';
  const items    = sale.items || [];
  const itemRows = items.map(item => `
    <tr>
      <td>${item.name} <span style="font-size:10px;color:#888">(${item.sku||''})</span></td>
      <td style="text-align:center">${item.qty}</td>
      <td class="amount">${fmtKES(item.unit_price)}</td>
      <td class="amount">${fmtKES(item.subtotal)}</td>
    </tr>`).join('');

  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Gift Shop ${isCash ? 'Receipt' : 'Sales Invoice'}</div>
    <div class="doc-number">Sale Ref: GS-${sale.sale_ref} &nbsp;|&nbsp; ${new Date(sale.created_at||Date.now()).toLocaleString('en-KE')}</div>

    <div class="section-title">Customer</div>
    <div class="meta-grid">
      ${sale.customer_name||sale.guest_name_str ? `<div class="meta-row"><span class="meta-label">Name:</span><span class="meta-value">${sale.customer_name||sale.guest_name_str}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Served By:</span><span class="meta-value">${sale.served_by_name||'—'}</span></div>
      <div class="meta-row"><span class="meta-label">Payment:</span><span class="meta-value">${(sale.payment_mode||'').toUpperCase().replace('_',' ')}</span></div>
      ${sale.transaction_code ? `<div class="meta-row"><span class="meta-label">Tx Code:</span><span class="meta-value">${sale.transaction_code}</span></div>` : ''}
    </div>

    <div class="section-title">Items Purchased</div>
    <table>
      <thead><tr><th>Item</th><th style="text-align:center">Qty</th><th class="amount">Unit Price</th><th class="amount">Total</th></tr></thead>
      <tbody>${itemRows}</tbody>
      <tfoot><tr class="total-row"><td colspan="3">TOTAL</td><td class="amount">${fmtKES(sale.total)}</td></tr></tfoot>
    </table>

    ${isCash ? `<div style="margin:14px 0;padding:10px 12px;border:2px solid #2ecc71;border-radius:4px;background:#0a180a;"><div style="font-size:11px;color:#2ecc71;letter-spacing:2px;text-transform:uppercase;margin-bottom:2px;">✓ CASH RECEIVED</div><div style="font-size:14px;font-weight:700;color:#2ecc71;">${fmtKES(sale.total)}</div></div>` : sale.payment_mode==='room_charge' ? `<div style="margin:14px 0;padding:10px 12px;border:1px solid #e67e22;border-radius:4px;background:#1a1000;"><div style="font-size:11px;color:#e67e22;margin-bottom:2px;">🏨 CHARGED TO ROOM</div><div style="font-size:12px;color:#aaa;">Added to guest folio — payable at checkout</div></div>` : ''}

    <div class="footer">
      ${branding.name} Gift Shop<br>
      ${branding.phone} &nbsp;·&nbsp; ${branding.address}<br>
      Thank you for your purchase!
    </div>`, branding, isApproved);
}

// ─── Store Issuance Voucher ───────────────────────────────────────────────────
export function printIssuanceVoucher(issuance) {
  const branding = getBranding();
  const isApproved = ['approved','issued'].includes(issuance.status);
  const approverRole = isApproved ? (issuance.approved_by_role || 'storekeeper') : null;
  const DEPT_LABELS = {
    kitchen:'Kitchen', bar:'Bar', housekeeping:'Housekeeping', restaurant:'Restaurant',
    reception:'Reception', conference:'Conference', maintenance:'Maintenance',
    pool:'Pool & Recreation', spa:'Spa & Wellness', gift_shop:'Gift Shop',
    admin:'Administration', other:'Other',
  };

  const items = issuance.issuance_items || [];
  const totalValue = items.reduce((s, i) => s + parseFloat(i.total_cost || 0), 0);

  const itemRows = items.map(line => `
    <tr>
      <td>${line.item_name || '—'}</td>
      <td style="text-align:center;font-weight:700;">${parseFloat(line.qty_issued)}</td>
      <td style="text-align:center;">${line.item_unit || ''}</td>
      <td class="amount">${fmtKES(line.unit_cost)}</td>
      <td class="amount" style="font-weight:700;">${fmtKES(line.total_cost)}</td>
    </tr>`).join('');

  openPrint(`
    ${buildHeader(branding)}
    <div class="doc-title" style="color:#006633;">STORE ISSUANCE VOUCHER</div>
    <div class="doc-number">Ref: ${issuance.ref} &nbsp;|&nbsp; ${issuance.issued_at ? new Date(issuance.issued_at).toLocaleString('en-KE') : new Date().toLocaleString('en-KE')}</div>

    <div class="section-title">Issuance Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Department:</span><span class="meta-value" style="font-weight:700;">${DEPT_LABELS[issuance.department] || issuance.department || '—'}</span></div>
      ${issuance.requested_by ? `<div class="meta-row"><span class="meta-label">Requested By:</span><span class="meta-value">${issuance.requested_by}</span></div>` : ''}
      <div class="meta-row"><span class="meta-label">Issued By:</span><span class="meta-value">${issuance.issued_by_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Status:</span><span class="meta-value" style="font-weight:700;text-transform:uppercase;">${issuance.status}</span></div>
      ${issuance.purpose ? `<div class="meta-row"><span class="meta-label">Purpose:</span><span class="meta-value">${issuance.purpose}</span></div>` : ''}
    </div>

    <div class="section-title">Items Issued</div>
    <table>
      <thead>
        <tr>
          <th>Item Description</th>
          <th style="text-align:center">Qty</th>
          <th style="text-align:center">Unit</th>
          <th class="amount">Unit Cost</th>
          <th class="amount">Total Cost</th>
        </tr>
      </thead>
      <tbody>${itemRows}</tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="4">TOTAL STOCK VALUE ISSUED</td>
          <td class="amount">${fmtKES(totalValue)}</td>
        </tr>
      </tfoot>
    </table>

    <div style="margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:24px;">
      <div style="border-top:1px solid #333;padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:#666;text-transform:uppercase;">Issued By (Storekeeper)</div>
        <div style="font-size:11px;color:#333;margin-top:4px;">${issuance.issued_by_name || '________________________'}</div>
      </div>
      <div style="border-top:1px solid #333;padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:#666;text-transform:uppercase;">Received By (Department)</div>
        <div style="font-size:11px;color:#333;margin-top:4px;color:#bbb;">________________________</div>
      </div>
    </div>

    <div class="footer">
      ${branding.name} · Store Issuance Voucher<br>
      ${branding.phone} &nbsp;·&nbsp; ${branding.address}<br>
      This is a computer-generated internal document.
    </div>`, branding, isApproved, approverRole);
}

export function printShoppingList(sl) {
  const branding = getBranding();
  const isApproved = ['approved','purchased'].includes(sl.status);
  const approverRole = isApproved ? (sl.approved_by_role || 'accountant') : null;
  const items = sl.items || [];
  const totalEst = items.reduce((s, i) => s + (parseFloat(i.qty_required)||0)*(parseFloat(i.estimated_cost)||0), 0);

  const statusColor = sl.status === 'approved' ? '#006600' : sl.status === 'purchased' ? '#003399' : '#c8a96e';

  const itemRows = items.map((item, idx) => `
    <tr style="background:${idx%2===0?'#fff':'#f9f9f9'}">
      <td>${item.item_name}</td>
      <td style="text-align:center;color:#c00;">${parseFloat(item.current_stock||0)} ${item.unit}</td>
      <td style="text-align:center;">${parseFloat(item.reorder_level||0)} ${item.unit}</td>
      <td style="text-align:center;font-weight:700;">${parseFloat(item.qty_required)} ${item.unit}</td>
      <td class="amount">${fmtKES(item.estimated_cost)}</td>
      <td class="amount" style="font-weight:700;">${fmtKES(item.line_total)}</td>
      <td style="font-size:9px;color:#666;">${item.notes||''}</td>
    </tr>`).join('');

  openPrint(`
    ${buildHeader(branding)}
    <div class="doc-title" style="color:#006600;">APPROVED SHOPPING LIST</div>
    <div class="doc-number">Ref: ${sl.ref} &nbsp;|&nbsp; ${new Date(sl.created_at).toLocaleString('en-KE')}</div>
    <div style="display:inline-block;padding:4px 12px;border:1px solid ${statusColor};border-radius:3px;color:${statusColor};font-weight:700;font-size:10px;letter-spacing:2px;text-transform:uppercase;margin-bottom:12px;">${sl.status}</div>

    <div class="section-title">Request Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Title:</span><span class="meta-value" style="font-weight:700;">${sl.title}</span></div>
      <div class="meta-row"><span class="meta-label">Created By:</span><span class="meta-value">${sl.created_by_name}</span></div>
      ${sl.approved_by_name && sl.approved_by_name !== '—' ? `<div class="meta-row"><span class="meta-label">Approved By:</span><span class="meta-value" style="color:#006600;font-weight:700;">${sl.approved_by_name}</span></div>` : ''}
      ${sl.approved_at ? `<div class="meta-row"><span class="meta-label">Approved At:</span><span class="meta-value">${new Date(sl.approved_at).toLocaleString('en-KE')}</span></div>` : ''}
      ${sl.notes ? `<div class="meta-row"><span class="meta-label">Notes:</span><span class="meta-value">${sl.notes}</span></div>` : ''}
    </div>

    <div class="section-title">Items to Purchase</div>
    <table>
      <thead>
        <tr>
          <th>Item Description</th>
          <th style="text-align:center">Current Stock</th>
          <th style="text-align:center">Reorder Level</th>
          <th style="text-align:center">Qty to Buy</th>
          <th class="amount">Est. Cost/Unit</th>
          <th class="amount">Line Total</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>${itemRows}</tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="5">TOTAL ESTIMATED PURCHASE COST</td>
          <td class="amount">${fmtKES(totalEst)}</td>
          <td></td>
        </tr>
      </tfoot>
    </table>

    <div style="margin-top:32px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;">
      <div style="border-top:1px solid #333;padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:#666;text-transform:uppercase;">Prepared By (Storekeeper)</div>
        <div style="font-size:11px;color:#333;margin-top:4px;">${sl.created_by_name || '________________________'}</div>
      </div>
      <div style="border-top:1px solid #333;padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:#006600;text-transform:uppercase;">Approved By (Manager)</div>
        <div style="font-size:11px;color:#006600;font-weight:700;margin-top:4px;">${sl.approved_by_name || '________________________'}</div>
      </div>
      <div style="border-top:1px solid #333;padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:#666;text-transform:uppercase;">Purchased By</div>
        <div style="font-size:11px;color:#333;margin-top:4px;">________________________</div>
      </div>
    </div>

    <div class="footer">
      ${branding.name} · Approved Shopping List · ${sl.ref}<br>
      ${branding.phone} &nbsp;·&nbsp; ${branding.address}<br>
      This document is only valid after manager approval. Ref: ${sl.ref}
    </div>`, branding, isApproved, approverRole);
}

// ── 12. Complimentary Bill Authorization ─────────────────────────────────────
export function printComplimentaryAuthorization(comp) {
  const branding = getBranding();
  const CAT_LABEL = {
    vip_guest:'VIP Guest', complaint:'Complaint Resolution',
    staff_meal:'Staff Meal', marketing:'Marketing / Promotion',
    error:'Billing Error', other:'Other',
  };
  const isApproved = comp.status === 'approved';
  const approverRole = isApproved ? (comp.approved_by_role || 'manager') : null;

  openPrint(`
    <div class="watermark">${branding.name.split(' ')[0].toUpperCase()}</div>
    ${buildHeader(branding)}
    <div class="doc-title">Complimentary Bill Authorization</div>
    <div class="doc-number">Ref: <strong>${comp.ref}</strong> &nbsp;|&nbsp; Printed: ${now()}</div>

    <div style="text-align:center;margin:14px 0;">
      <span class="stamp ${isApproved ? '' : 'stamp-pending'}" style="font-size:13px;letter-spacing:3px;">
        ${isApproved ? '✓ APPROVED' : comp.status === 'rejected' ? '✕ REJECTED' : '⏳ PENDING APPROVAL'}
      </span>
    </div>

    <div class="section-title">Bill Details</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Reference:</span><span class="meta-value" style="font-family:monospace;font-weight:700;color:#8b6914">${comp.ref}</span></div>
      <div class="meta-row"><span class="meta-label">Guest / Client:</span><span class="meta-value">${comp.guest_name}</span></div>
      <div class="meta-row"><span class="meta-label">Category:</span><span class="meta-value">${CAT_LABEL[comp.category] || comp.category}</span></div>
      <div class="meta-row"><span class="meta-label">Amount Comped:</span><span class="meta-value" style="font-weight:700;font-size:14px;">${fmtKES(comp.amount)}</span></div>
      <div class="meta-row"><span class="meta-label">Requested By:</span><span class="meta-value">${comp.requested_by_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Submitted:</span><span class="meta-value">${comp.created_at ? new Date(comp.created_at).toLocaleString('en-KE') : '—'}</span></div>
      ${isApproved ? `<div class="meta-row"><span class="meta-label">Approved By:</span><span class="meta-value" style="color:#155724;font-weight:700;">${comp.approved_by_name || '—'}</span></div>` : ''}
      ${isApproved && comp.approved_at ? `<div class="meta-row"><span class="meta-label">Approved At:</span><span class="meta-value">${new Date(comp.approved_at).toLocaleString('en-KE')}</span></div>` : ''}
    </div>

    <div class="section-title">Reason / Justification</div>
    <div style="padding:10px 14px;background:#f8f6f0;border-left:3px solid #c8a96e;border-radius:2px;font-size:12px;color:#333;line-height:1.7;margin-bottom:14px;">${comp.reason}</div>

    ${isApproved ? `
    <div class="section-title">Accounting Impact</div>
    <table>
      <thead><tr><th>Description</th><th class="amount">Amount</th></tr></thead>
      <tbody>
        <tr><td>Complimentary — ${CAT_LABEL[comp.category]||comp.category} — ${comp.guest_name}</td><td class="amount">${fmtKES(comp.amount)}</td></tr>
      </tbody>
      <tfoot><tr class="total-row"><td>TOTAL EXPENSED</td><td class="amount">${fmtKES(comp.amount)}</td></tr></tfoot>
    </table>
    <div class="notice-box notice-paid">✓ This complimentary has been approved and auto-recorded as an expense in accounting.</div>
    ` : ''}

    <div style="margin-top:32px;display:grid;grid-template-columns:1fr 1fr;gap:24px;">
      <div style="border-top:1px solid #333;padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:#666;text-transform:uppercase;">Requested By</div>
        <div style="font-size:11px;color:#333;margin-top:4px;">${comp.requested_by_name || '________________________'}</div>
      </div>
      <div style="border-top:1px solid ${isApproved ? '#006600' : '#333'};padding-top:6px;text-align:center;">
        <div style="font-size:9px;letter-spacing:2px;color:${isApproved ? '#006600' : '#666'};text-transform:uppercase;">Approved By (Manager)</div>
        <div style="font-size:11px;color:${isApproved ? '#006600' : '#ccc'};font-weight:700;margin-top:4px;">${isApproved ? (comp.approved_by_name || '________________________') : '________________________'}</div>
      </div>
    </div>

    <div class="footer">
      ${branding.name} &nbsp;·&nbsp; ${branding.address}<br>
      ${branding.email} &nbsp;·&nbsp; ${branding.phone}<br>
      This is a computer-generated complimentary authorization document
    </div>`, branding, isApproved, approverRole);
}

// ── 13. Pool / Swimming Entry Receipt ─────────────────────────────────────────
export function printPoolReceipt(entry) {
  const b = getBranding();
  const isApproved = false;
  const RATE_LABELS = {
    adult_resident: 'Adult Resident', adult_nonresident: 'Adult Non-Resident',
    child_resident: 'Child Resident', child_nonresident: 'Child Non-Resident',
    hotel_guest: 'Hotel Guest (Free)', complimentary: 'Complimentary',
  };
  const modeLabel = (m) => ({ cash:'Cash', mpesa:'M-Pesa', card:'Card', room_charge:'Room Charge', complimentary:'Complimentary' }[m] || m);
  const html = `
    ${buildHeader(b)}
    <div class="watermark">POOL</div>
    <div class="doc-title">Pool & Recreation Entry</div>
    <div class="doc-number">Receipt #${entry.id} &nbsp;·&nbsp; ${now()}</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Visitor</span><span class="meta-value">${entry.visitor_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Type</span><span class="meta-value">${RATE_LABELS[entry.visitor_type] || entry.visitor_type}</span></div>
      <div class="meta-row"><span class="meta-label">Adults</span><span class="meta-value">${entry.adults || 0}</span></div>
      <div class="meta-row"><span class="meta-label">Children</span><span class="meta-value">${entry.children || 0}</span></div>
      <div class="meta-row"><span class="meta-label">Payment</span><span class="meta-value">${modeLabel(entry.payment_mode)}</span></div>
      ${entry.tx_code ? `<div class="meta-row"><span class="meta-label">Ref</span><span class="meta-value">${entry.tx_code}</span></div>` : ''}
    </div>
    <table>
      <thead><tr><th>Description</th><th class="amount">Amount</th></tr></thead>
      <tbody>
        ${entry.adults > 0 ? `<tr><td>Adults × ${entry.adults}</td><td class="amount">${fmtKES((entry.amount || 0) * (entry.adults / ((entry.adults || 1) + (entry.children || 0))))}</td></tr>` : ''}
        ${entry.children > 0 ? `<tr><td>Children × ${entry.children}</td><td class="amount">${fmtKES((entry.amount || 0) * (entry.children / ((entry.adults || 0) + (entry.children || 1))))}</td></tr>` : ''}
        <tr class="total-row"><td>TOTAL</td><td class="amount">${fmtKES(entry.amount || 0)}</td></tr>
      </tbody>
    </table>
    ${entry.payment_mode === 'room_charge' ? `<div class="notice-box notice-request">This amount has been charged to the guest's room folio.</div>` : `<div class="notice-box notice-paid">✓ PAID — ${modeLabel(entry.payment_mode)}</div>`}
    <div class="footer">
      Pool hours: 6:00 AM – 10:00 PM daily · Valid for date of issue only<br/>
      ${b.name} &nbsp;·&nbsp; ${b.phone} &nbsp;·&nbsp; ${b.email}
    </div>`;
  openPrint(html, b, isApproved);
}

// ── 14. Kids Amenities Booking Receipt ────────────────────────────────────────
export function printKidsReceipt(booking) {
  const b = getBranding();
  const isApproved = false;
  const modeLabel = (m) => ({ cash:'Cash', mpesa:'M-Pesa', card:'Card', room_charge:'Room Charge', complimentary:'Complimentary' }[m] || m);
  const items = booking.items || [];
  const html = `
    ${buildHeader(b)}
    <div class="watermark">KIDS ZONE</div>
    <div class="doc-title">Kids Amenities Receipt</div>
    <div class="doc-number">Booking #${booking.id} &nbsp;·&nbsp; ${now()}</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Child</span><span class="meta-value">${booking.child_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Age</span><span class="meta-value">${booking.child_age ? booking.child_age + ' yrs' : '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Parent / Guardian</span><span class="meta-value">${booking.parent_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Payment</span><span class="meta-value">${modeLabel(booking.payment_mode)}</span></div>
      ${booking.tx_code ? `<div class="meta-row"><span class="meta-label">Ref</span><span class="meta-value">${booking.tx_code}</span></div>` : ''}
    </div>
    <table>
      <thead><tr><th>Activity</th><th style="text-align:center">Qty</th><th class="amount">Unit Price</th><th class="amount">Total</th></tr></thead>
      <tbody>
        ${items.length > 0
          ? items.map(it => `<tr><td>${it.amenity_name || it.name || '—'}</td><td style="text-align:center">${it.qty || 1}</td><td class="amount">${fmtKES(it.price)}</td><td class="amount">${fmtKES((it.price || 0) * (it.qty || 1))}</td></tr>`).join('')
          : `<tr><td colspan="4" style="text-align:center;color:#888">Kids zone booking</td></tr>`
        }
        <tr class="total-row"><td colspan="3">TOTAL</td><td class="amount">${fmtKES(booking.amount || 0)}</td></tr>
      </tbody>
    </table>
    ${booking.payment_mode === 'room_charge' ? `<div class="notice-box notice-request">Charged to guest room folio.</div>` : `<div class="notice-box notice-paid">✓ PAID — ${modeLabel(booking.payment_mode)}</div>`}
    <div class="footer">
      Kids Zone — safe, supervised fun for all ages<br/>
      ${b.name} &nbsp;·&nbsp; ${b.phone}
    </div>`;
  openPrint(html, b, isApproved);
}

// ── 15. Expense / Petty Cash Voucher (Accountant) ────────────────────────────
export function printExpenseVoucher(expense) {
  const b = getBranding();
  const isApproved = false;
  const CAT_LABELS = {
    food:'Food & Beverage', utilities:'Utilities', maintenance:'Maintenance & Repairs',
    salaries:'Salaries & Wages', marketing:'Marketing', supplies:'Supplies & Stationery',
    transport:'Transport & Fuel', entertainment:'Entertainment', insurance:'Insurance',
    rent:'Rent & Lease', equipment:'Equipment', other:'Other',
  };
  const modeLabel = (m) => ({ cash:'Cash', mpesa:'M-Pesa', bank_transfer:'Bank Transfer', cheque:'Cheque', card:'Card' }[m] || m || '—');
  const html = `
    ${buildHeader(b)}
    <div class="doc-title">Expense Voucher</div>
    <div class="doc-number">EXP-${String(expense.id).padStart(4,'0')} &nbsp;·&nbsp; ${expense.expense_date || now()}</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Category</span><span class="meta-value">${CAT_LABELS[expense.category] || expense.category}</span></div>
      <div class="meta-row"><span class="meta-label">Date</span><span class="meta-value">${expense.expense_date || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Vendor</span><span class="meta-value">${expense.vendor || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Invoice #</span><span class="meta-value">${expense.invoice_number || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Payment Mode</span><span class="meta-value">${modeLabel(expense.payment_mode)}</span></div>
      <div class="meta-row"><span class="meta-label">Receipt Ref</span><span class="meta-value">${expense.receipt_ref || '—'}</span></div>
    </div>
    <div class="section-title">Description</div>
    <div style="padding:10px 12px;background:#f8f6f0;border-left:3px solid #c8a96e;font-size:13px;margin-bottom:14px;">${expense.description || '—'}</div>
    ${expense.notes ? `<div style="padding:8px 12px;background:#fffde7;border:1px dashed #f0a000;font-size:11px;margin-bottom:14px;border-radius:3px;"><strong>Notes:</strong> ${expense.notes}</div>` : ''}
    <table>
      <tbody>
        <tr class="total-row"><td>TOTAL AMOUNT</td><td class="amount">${fmtKES(expense.amount)}</td></tr>
      </tbody>
    </table>
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;margin-top:30px;text-align:center;">
      <div style="border-top:1px solid #333;padding-top:6px;font-size:10px;color:#888;">Prepared By</div>
      <div style="border-top:1px solid #333;padding-top:6px;font-size:10px;color:#888;">Approved By</div>
      <div style="border-top:1px solid #333;padding-top:6px;font-size:10px;color:#888;">Received By</div>
    </div>
    <div class="footer">
      Printed ${now()} &nbsp;·&nbsp; ${b.name} &nbsp;·&nbsp; PIN: ${b.pin}
    </div>`;
  openPrint(html, b, isApproved);
}

// ── 16. Daily Sales Summary (Restaurant Manager / Admin) ─────────────────────
export function printDailySalesSummary(data) {
  const b = getBranding();
  const isApproved = false;
  // data: { date, bills, totalRevenue, byMode, byWaiter, pendingCount }
  const date = data.date || new Date().toLocaleDateString('en-KE', { dateStyle:'full' });
  const byMode  = data.byMode  || [];
  const byWaiter= data.byWaiter|| [];
  const html = `
    ${buildHeader(b)}
    <div class="doc-title">Daily Sales Summary</div>
    <div class="doc-number">${date} &nbsp;·&nbsp; Printed ${now()}</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Total Revenue</span><span class="meta-value" style="font-weight:800;font-size:14px;color:#2ecc71">${fmtKES(data.totalRevenue || 0)}</span></div>
      <div class="meta-row"><span class="meta-label">Total Bills</span><span class="meta-value">${data.billCount || 0}</span></div>
      <div class="meta-row"><span class="meta-label">Paid Bills</span><span class="meta-value" style="color:#2ecc71">${data.paidCount || 0}</span></div>
      <div class="meta-row"><span class="meta-label">Pending</span><span class="meta-value" style="color:#e67e22">${data.pendingCount || 0}</span></div>
    </div>
    <div class="section-title">Revenue by Payment Mode</div>
    <table>
      <thead><tr><th>Mode</th><th class="amount">Amount</th><th class="amount">Transactions</th></tr></thead>
      <tbody>
        ${byMode.map(m => `<tr><td style="text-transform:capitalize">${m.mode || '—'}</td><td class="amount">${fmtKES(m.amount)}</td><td class="amount">${m.count || '—'}</td></tr>`).join('') || '<tr><td colspan="3" style="text-align:center;color:#888">No data</td></tr>'}
        <tr class="total-row"><td>TOTAL</td><td class="amount">${fmtKES(data.totalRevenue || 0)}</td><td class="amount">${data.paidCount || 0}</td></tr>
      </tbody>
    </table>
    ${byWaiter.length > 0 ? `
    <div class="section-title">Performance by Waiter</div>
    <table>
      <thead><tr><th>Waiter</th><th class="amount">Revenue</th><th class="amount">Orders</th></tr></thead>
      <tbody>
        ${byWaiter.map(w => `<tr><td>${w.name}</td><td class="amount">${fmtKES(w.revenue)}</td><td class="amount">${w.orders}</td></tr>`).join('')}
      </tbody>
    </table>` : ''}
    <div class="footer">
      This is a computer-generated summary &nbsp;·&nbsp; ${b.name}
    </div>`;
  openPrint(html, b, isApproved);
}

// ── 17. Laundry / Damage Charge Receipt (Housekeeping) ───────────────────────
export function printHousekeepingCharge(charge) {
  const b = getBranding();
  const isApproved = false;
  const html = `
    ${buildHeader(b)}
    <div class="doc-title">Housekeeping ${charge.type === 'damage' ? 'Damage Report' : 'Service Charge'}</div>
    <div class="doc-number">HK-${String(charge.id || 0).padStart(4,'0')} &nbsp;·&nbsp; ${now()}</div>
    <div class="meta-grid">
      <div class="meta-row"><span class="meta-label">Room</span><span class="meta-value">${charge.room_number || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Guest</span><span class="meta-value">${charge.guest_name || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Type</span><span class="meta-value">${charge.type === 'damage' ? 'Damage / Loss' : 'Laundry / Service'}</span></div>
      <div class="meta-row"><span class="meta-label">Logged By</span><span class="meta-value">${charge.logged_by || '—'}</span></div>
      <div class="meta-row"><span class="meta-label">Date</span><span class="meta-value">${charge.date || now()}</span></div>
      <div class="meta-row"><span class="meta-label">Status</span><span class="meta-value">${charge.status || 'Pending'}</span></div>
    </div>
    <div class="section-title">Description</div>
    <div style="padding:10px 12px;background:#f8f6f0;border-left:3px solid #c8a96e;font-size:13px;margin-bottom:14px;">${charge.description || '—'}</div>
    <table>
      <tbody>
        <tr class="total-row"><td>CHARGE AMOUNT</td><td class="amount">${fmtKES(charge.amount || 0)}</td></tr>
      </tbody>
    </table>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:30px;margin-top:30px;text-align:center;">
      <div style="border-top:1px solid #333;padding-top:6px;font-size:10px;color:#888;">Housekeeper Signature</div>
      <div style="border-top:1px solid #333;padding-top:6px;font-size:10px;color:#888;">Guest / Manager Signature</div>
    </div>
    <div class="footer">
      ${b.name} &nbsp;·&nbsp; ${b.phone}
    </div>`;
  openPrint(html, b, isApproved);
}

// ── 18. Payslip ───────────────────────────────────────────────────────────────
export function printPayslip(emp, calc, selectedMonth) {
  const approverRole = 'director';
  const b = getBranding();
  const isApproved = true;
  const fmtN = (n) => 'KES ' + (n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });
  const earningRow = (label, val) => val
    ? `<tr><td class="label">${label}</td><td class="value" style="color:#27ae60">${fmtN(val)}</td></tr>` : '';
  const deductRow  = (label, val) => val
    ? `<tr><td class="label">${label}</td><td class="value" style="color:#c0392b">− ${fmtN(val)}</td></tr>` : '';

  const html = `
    ${buildHeader(b)}
    <div class="doc-title">Payslip</div>
    <div class="doc-number">${selectedMonth || new Date().toLocaleDateString('en-KE', { month:'long', year:'numeric' })} &nbsp;·&nbsp; Printed ${now()}</div>
    <div class="content-wrap">

      <div class="kpi-row">
        <div class="kpi blue"><div class="kpi-label">Gross Pay</div><div class="kpi-value">${fmtN(calc.grossPay)}</div></div>
        <div class="kpi red"><div class="kpi-label">Total Deductions</div><div class="kpi-value">${fmtN(calc.totalDeductions)}</div></div>
        <div class="kpi green"><div class="kpi-label">Net Pay</div><div class="kpi-value">${fmtN(calc.netPay)}</div></div>
        <div class="kpi gold"><div class="kpi-label">Period</div><div class="kpi-value" style="font-size:13px">${selectedMonth || '—'}</div></div>
      </div>

      <div class="meta-grid">
        <div class="meta-row"><span class="meta-label">Employee</span><span class="meta-value" style="font-size:13px">${emp.name}</span></div>
        <div class="meta-row"><span class="meta-label">Employee ID</span><span class="meta-value">${emp.emp_id || '—'}</span></div>
        <div class="meta-row"><span class="meta-label">Job Title</span><span class="meta-value">${emp.job_title || '—'}</span></div>
        <div class="meta-row"><span class="meta-label">KRA PIN</span><span class="meta-value">${emp.kra_pin || '—'}</span></div>
        <div class="meta-row"><span class="meta-label">Department</span><span class="meta-value">${emp.department || '—'}</span></div>
        <div class="meta-row"><span class="meta-label">NSSF No.</span><span class="meta-value">${emp.nssf_number || '—'}</span></div>
        <div class="meta-row"><span class="meta-label">NHIF / SHA No.</span><span class="meta-value">${emp.nhif_number || '—'}</span></div>
        <div class="meta-row"><span class="meta-label">Pay Period</span><span class="meta-value">${selectedMonth || '—'}</span></div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px">
        <div class="card">
          <div class="card-header">Earnings</div>
          <table>
            <thead><tr><th>Description</th><th class="amount">Amount (KES)</th></tr></thead>
            <tbody>
              ${earningRow('Basic Salary', calc.basic)}
              ${earningRow('House Allowance', calc.house)}
              ${earningRow('Transport Allowance', calc.transport)}
              ${earningRow('Medical Allowance', calc.medical)}
              ${earningRow('Overtime Pay', calc.overtime)}
              ${earningRow('Bonus', calc.bonus)}
              <tr class="subtotal-row"><td style="font-weight:700">GROSS PAY</td><td class="amount" style="color:#1565c0;font-weight:700">${fmtN(calc.grossPay)}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="card">
          <div class="card-header accent">Deductions</div>
          <table>
            <thead><tr><th>Description</th><th class="amount">Amount (KES)</th></tr></thead>
            <tbody>
              ${deductRow('PAYE Tax (KRA)', calc.paye)}
              ${deductRow('NSSF', calc.nssf)}
              ${deductRow('NHIF / SHA', calc.nhif)}
              ${deductRow('Housing Levy (1.5%)', calc.housingLevy)}
              ${deductRow('Loan Deduction', calc.loanDeduct)}
              ${deductRow('Salary Advance Recovery', calc.advanceDeduct)}
              <tr class="subtotal-row"><td style="font-weight:700">TOTAL DEDUCTIONS</td><td class="amount" style="color:#c62828;font-weight:700">${fmtN(calc.totalDeductions)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="card">
        <table><tbody>
          <tr class="total-row">
            <td style="font-size:14px;letter-spacing:.5px">NET PAY</td>
            <td class="amount" style="font-size:16px;color:#a5d6a7">${fmtN(calc.netPay)}</td>
          </tr>
        </tbody></table>
      </div>

      <div style="margin:12px 0;padding:10px 14px;background:#fff;border-radius:6px;border-left:4px solid #b8860b;font-size:11px;color:#555;line-height:1.9;box-shadow:0 1px 4px rgba(0,0,0,.06)">
        <strong style="color:#1a1a2e">Banking Details</strong><br>
        <strong>Bank:</strong> ${emp.bank_name || '—'} &nbsp;·&nbsp;
        <strong>Account:</strong> ${emp.bank_account || '—'} &nbsp;·&nbsp;
        <strong>Branch:</strong> ${emp.bank_branch || '—'}
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:28px;text-align:center;">
        <div style="border-top:2px solid #e0e4f0;padding-top:8px;font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.8px">Authorised Signatory</div>
        <div style="border-top:2px solid #e0e4f0;padding-top:8px;font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.8px">Employee Acknowledgement</div>
      </div>

    </div>
    <div class="footer">
      <span>${b.name}</span>
      <span>This is a computer-generated payslip — PIN: ${b.pin}</span>
      <span>Printed ${now()}</span>
    </div>`;
  openPrint(html, b, isApproved, approverRole);
}

// ── 19. Room Status Sheet (Housekeeping) ─────────────────────────────────────
export function printRoomStatusSheet(rooms) {
  const b = getBranding();
  const isApproved = false;
  const statusColor = { dirty:'#e74c3c', clean:'#2ecc71', inspecting:'#f39c12', 'out-of-order':'#7f8c8d' };
  const clean    = rooms.filter(r => r.status === 'clean').length;
  const dirty    = rooms.filter(r => r.status === 'dirty').length;
  const inspect  = rooms.filter(r => r.status === 'inspecting').length;
  const oor      = rooms.filter(r => r.status === 'out-of-order').length;

  const summaryHtml = `
    <div style="display:flex;gap:24px;margin-bottom:16px;padding:12px 16px;background:#f8f6f0;border-left:3px solid ${b.primary || '#c8a96e'};border-radius:2px;">
      ${[['Clean', clean, '#2ecc71'], ['Dirty', dirty, '#e74c3c'], ['Inspecting', inspect, '#f39c12'], ['Out of Order', oor, '#7f8c8d'], ['Total', rooms.length, '#333']]
        .map(([lbl, n, col]) => `<div style="text-align:center;flex:1"><div style="font-size:22px;font-weight:900;color:${col}">${n}</div><div style="font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.5px">${lbl}</div></div>`).join('')}
    </div>`;

  const rowsHtml = rooms.map(r => `
    <tr>
      <td style="font-weight:700">${r.number}</td>
      <td>${r.type || '—'}</td>
      <td>Floor ${r.floor || '—'}</td>
      <td style="font-weight:700;color:${statusColor[r.status] || '#333'}">${(r.status || '—').toUpperCase()}</td>
      <td>${r.guest_name || '—'}</td>
      <td style="color:#aaa;font-size:10px;min-width:100px"></td>
    </tr>`).join('');

  const html = `
    ${buildHeader(b)}
    <div class="doc-title">Room Status Sheet</div>
    <div class="doc-number">Printed ${now()}</div>
    ${summaryHtml}
    <table>
      <thead>
        <tr>
          <th>Room</th><th>Type</th><th>Floor</th><th>Status</th><th>Guest</th><th>Staff Sign-off</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div class="footer">${b.name} &nbsp;·&nbsp; ${b.phone}</div>`;
  openPrint(html, b, isApproved);
}
